import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Save, X, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../contexts/LanguageContext';
import { useDiaryReading, useDiaryMutations, DIARY_QUERY_KEYS } from '../hooks/useDiary';
import aiService from '../services/aiService';
import { AIExplanationDisplay } from '../utils/aiExplanationFormatter';
import PaymentModal from './PaymentModal';
import logger from '../utils/logger';

const ReadingDetail = () => {
  const { id } = useParams();
  const { t, currentLanguage } = useLanguage();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ question: '', notes: '' });
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiError, setAiError] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // React Query hooks
  const { 
    data: reading, 
    isLoading: loading, 
    error: queryError 
  } = useDiaryReading(id);

  const { updateReading } = useDiaryMutations();
  
  const error = queryError?.message || '';

  // Update edit form when reading data loads
  useEffect(() => {
    if (reading) {
      setEditForm({
        question: reading.question || '',
        notes: reading.notes || ''
      });
    }
  }, [reading]);

  const handleSaveEdit = async () => {
    try {
      await updateReading.mutateAsync({ id, data: editForm });
      setIsEditing(false);
    } catch (err) {
      logger.error('Error updating reading:', err);
      // Error handling is done in the mutation
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      question: reading.question || '',
      notes: reading.notes || ''
    });
    setIsEditing(false);
  };

  const handleGenerateAIExplanation = async () => {
    if (!reading || isGeneratingAI) return;
    
    setIsGeneratingAI(true);
    setAiError('');
    
    try {
      // Format the reading data for AI processing
      const spreadData = aiService.formatSpreadForAI({
        spreadName: reading.spread || 'Custom Reading',
        spreadType: reading.spread || 'custom',
        cards: reading.cards.map(cardData => ({
          name: cardData.card.name,
          meaning: cardData.card.meaning,
          position: cardData.card.position || 'upright',
          image: cardData.card.image,
          link: cardData.card.link
        })),
        question: reading.question || '',
        notes: reading.notes || '',
        cardCount: reading.cards.length,
        spreadInterpretations: reading.cards.map(cardData => cardData.position)
      });

      // Generate AI explanation with current language
      const response = await aiService.generateSpreadExplanation(spreadData, currentLanguage);
      
      // Update the reading with AI explanation
      const updatedReading = await updateReading.mutateAsync({ 
        id, 
        data: { 
          aiExplanation: response.explanation 
        } 
      });
      
      // Force a cache invalidation to ensure fresh data is displayed
      await queryClient.invalidateQueries({ 
        queryKey: DIARY_QUERY_KEYS.reading(id) 
      });
      
      logger.debug('AI explanation generated and cache updated successfully');
      
      // Scroll to the Taroca explanation section after a short delay to ensure it's rendered
      setTimeout(() => {
        const explanationElement = document.getElementById('taroca-explanation');
        if (explanationElement) {
          explanationElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 500); // 500ms delay to ensure the component has re-rendered with the new data
      
    } catch (error) {
      logger.error('Error generating AI explanation:', error);
      setAiError(error.message || 'Failed to generate AI explanation');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Payment functions
  const handleAskTaroca = () => {
    if (reading?.ai_explanation) {
      // If there's already an AI explanation, no need to pay again
      handleGenerateAIExplanation();
    } else {
      // If no AI explanation exists, open payment modal
      setShowPaymentModal(true);
    }
  };

  const handlePaymentSuccess = async (successMessage) => {
    logger.payment('Payment successful for reading:', id);
    setShowPaymentModal(false);
    setIsGeneratingAI(true);
    
    try {
      // Format the reading data for AI processing
      const spreadData = aiService.formatSpreadForAI({
        spreadName: reading.spread || 'Custom Reading',
        spreadType: reading.spread || 'custom',
        cards: reading.cards.map(cardData => ({
          name: cardData.card.name,
          meaning: cardData.card.meaning,
          position: cardData.card.position || 'upright',
          image: cardData.card.image,
          link: cardData.card.link
        })),
        question: reading.question || '',
        notes: reading.notes || '',
        cardCount: reading.cards.length,
        spreadInterpretations: reading.cards.map(cardData => cardData.position)
      });

      // Call the AI service to generate explanation
      const response = await aiService.generateSpreadExplanation(spreadData, currentLanguage);
      
      // Update the reading with the AI explanation
      await updateReading.mutateAsync({ 
        id, 
        data: { 
          aiExplanation: response.explanation 
        } 
      });
      
      // Invalidate the query to refresh the data
      queryClient.invalidateQueries([DIARY_QUERY_KEYS.READING, id]);
      
      logger.debug('AI explanation generated and reading updated successfully');
      
      // Small delay to ensure the component re-renders with updated data
      setTimeout(() => {
        const element = document.getElementById('taroca-explanation');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
      
    } catch (error) {
      logger.error('Error generating AI explanation after payment:', error);
      setAiError(error.message || 'Failed to generate AI explanation');
    } finally {
      setIsGeneratingAI(false);
    }
    
    return { readingId: id };
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>{t('readingDetail.loading')}</p>
      </div>
    );
  }

  if (error || !reading) {
    return (
      <div className="card">
        <div className="error">
          {error || t('readingDetail.notFound')}
        </div>
        <Link to="/" className="btn btn-primary">
          <ArrowLeft size={16} />
          {t('readingDetail.backToDiary')}
        </Link>
      </div>
    );
  }

  return (
    <div className="reading-detail" style={{
      minHeight: '100vh',
      width: '100%',
      margin: '0',
      boxSizing: 'border-box',
      position: 'relative',
      overflowY: 'auto',
      overflowX: 'hidden',
      backgroundImage: window.innerWidth <= 768 ? `url(/images/bg_taroca.jpg)` : `url(/images/bg_taroca_desktop.jpg)`,
      backgroundSize: 'cover',
      backgroundPosition: 'top center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      padding: '2rem 2rem 4rem 2rem'
    }}>
      <div className="page-header">
        <Link to="/" className="btn btn-secondary">
          <ArrowLeft size={16} />
          {t('readingDetail.backToDiary')}
        </Link>
        
        <div className="header-actions">
          {!isEditing ? (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button 
                className="btn btn-primary"
                onClick={() => setIsEditing(true)}
              >
                <Edit size={16} />
                {t('readingDetail.editNotes')}
              </button>
            </div>
          ) : (
            <div className="edit-actions">
              <button 
                className="btn btn-primary"
                onClick={handleSaveEdit}
              >
                <Save size={16} />
                {t('readingDetail.save')}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={handleCancelEdit}
              >
                <X size={16} />
                {t('readingDetail.cancel')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="reading-header">
          <div className="reading-date">
            {(() => {
              try {
                const dateValue = reading.timestamp || reading.createdAt || reading.readingDate || new Date();
                const date = new Date(dateValue);
                if (isNaN(date.getTime())) {
                  // If date is invalid, use current date
                  const now = new Date();
                  return (
                    <>
                      <div className="date-day">{format(now, 'dd')}</div>
                      <div className="date-month-year">{format(now, 'MMM yyyy')}</div>
                    </>
                  );
                }
                return (
                  <>
                    <div className="date-day">{format(date, 'dd')}</div>
                    <div className="date-month-year">{format(date, 'MMM yyyy')}</div>
                  </>
                );
              } catch (error) {
                logger.error('Date formatting error in ReadingDetail:', error, 'Reading:', reading);
                const now = new Date();
                return (
                  <>
                    <div className="date-day">{format(now, 'dd')}</div>
                    <div className="date-month-year">{format(now, 'MMM yyyy')}</div>
                  </>
                );
              }
            })()}
          </div>
          <div className="reading-content-left">
            <h1>{reading.spread || t('readingDetail.defaultTitle')}</h1>
          </div>
        </div>

        {isEditing ? (
          <div className="edit-form">
            <div className="form-group">
              <label className="form-label">{t('readingDetail.questionAsked')}</label>
              <input
                type="text"
                className="form-input"
                value={editForm.question}
                onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                placeholder={t('readingDetail.questionPlaceholder')}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">{t('readingDetail.yourNotes')}</label>
              <textarea
                className="form-textarea"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder={t('readingDetail.notesPlaceholder')}
                rows={6}
              />
            </div>
          </div>
        ) : (
          <div className="reading-metadata">
            {reading.question && (
              <div className="question-section">
                <h3>{t('readingDetail.questionAsked')}</h3>
                <p>"{reading.question}"</p>
              </div>
            )}
            
            {reading.notes && (
              <div className="notes-section">
                <h3>{t('readingDetail.yourNotes')}</h3>
                <p>{reading.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Spread Overview Section */}
        <div className="spread-overview" style={{
          background: 'rgba(0,0,0,0.02)',
          backdropFilter: 'blur(5px)',
          padding: '2rem',
          borderRadius: '15px',
          marginBottom: '2rem',
          border: '1px solid rgba(0,0,0,0.1)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
        }}>
          <h3 style={{
            color: '#333', 
            textAlign: 'center', 
            marginBottom: '1.5rem',
            fontSize: window.innerWidth <= 480 ? '1.1rem' : window.innerWidth <= 768 ? '1.2rem' : '1.3rem'
          }}>
            {t('spreadDrawing.spreadOverview')}
          </h3>
          
          {/* Special layout for 10-card Celtic Cross spread */}
          {reading.cards.length === 10 ? (
            <div className="celtic-cross-layout" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 80px)',
              gap: '8px',
              justifyContent: 'center',
              alignItems: 'start',
              maxWidth: '400px',
              margin: '0 auto',
              position: 'relative'
            }}>
              {reading.cards.map((cardData, index) => {
                const cardName = cardData.card?.name || cardData.name || 'Unknown Card';
                
                // Multi-language reversed detection function
                const isCardReversed = (cardData, cardName) => {
                  // Check all possible reversed indicators
                  const reversedTranslations = ['reversed', '逆位置', '逆位'];
                  
                  // Check card.position
                  if (cardData.card?.position && reversedTranslations.includes(cardData.card.position)) {
                    return true;
                  }
                  
                  // Check cardData.position
                  if (cardData.position && reversedTranslations.includes(cardData.position)) {
                    return true;
                  }
                  
                  // Check if card name starts with "Reversed" in English
                  if (cardName.startsWith('Reversed')) {
                    return true;
                  }
                  
                  return false;
                };
                
                const isReversed = isCardReversed(cardData, cardName);
                
                // Get the position name
                const position = cardData.position || 
                               (cardData.card && cardData.card.position && !['upright', 'reversed'].includes(cardData.card.position) ? cardData.card.position : null) ||
                               `${t('spreadDrawing.cardPosition')} ${index + 1}`;
                
                // Get card image source
                const getCardImageSrc = (cardData) => {
                  if (cardData.card?.image) {
                    const imageUrl = cardData.card.image;
                    const fileName = imageUrl.split('/').pop();
                    return `/images/tarot-cards/${fileName}`;
                  }
                  
                  const cardName = cardData.card?.name || cardData.name || '';
                  const match = cardName.match(/^(\d+)\./);
                  if (match) {
                    return `/images/tarot-cards/image_${match[1]}.jpg`;
                  }
                  
                  return '/images/tarot-cards/image_1.jpg';
                };
                
                // Celtic Cross positioning with improved spacing
                const getCelticCrossPosition = (index) => {
                  switch(index) {
                    case 0: return { gridColumn: '2', gridRow: '3', paddingTop: '20px' }; // 1st card - center with top padding
                    case 1: return { gridColumn: '2', gridRow: '4', transform: 'rotate(-90deg)', zIndex: 2, paddingTop: '0px' }; // 2nd card - rotated -90 degrees at bottom of 1st card
                    case 2: return { gridColumn: '1', gridRow: '3', paddingTop: '20px' }; // 3rd card - left of center
                    case 3: return { gridColumn: '3', gridRow: '3', paddingTop: '20px' }; // 4th card - right of center
                    case 4: return { gridColumn: '2', gridRow: '2', paddingTop: '20px' }; // 5th card - above center
                    case 5: return { gridColumn: '2', gridRow: '5', paddingTop: '20px' }; // 6th card - below center (moved down)
                    case 6: return { gridColumn: '4', gridRow: '5', paddingTop: '20px' }; // 7th card - right of 6th
                    case 7: return { gridColumn: '4', gridRow: '4', paddingTop: '20px' }; // 8th card - above 7th
                    case 8: return { gridColumn: '4', gridRow: '3', paddingTop: '20px' }; // 9th card - above 8th
                    case 9: return { gridColumn: '4', gridRow: '2', paddingTop: '20px' }; // 10th card - above 9th
                    default: return { gridColumn: '1', gridRow: '1', paddingTop: '20px' };
                  }
                };
                
                const gridPosition = getCelticCrossPosition(index);
                
                return (
                  <div key={index} className="celtic-cross-card" style={{
                    gridColumn: gridPosition.gridColumn,
                    gridRow: gridPosition.gridRow,
                    paddingTop: gridPosition.paddingTop,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: gridPosition.zIndex || 1
                  }}>
                    <div style={{
                      background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                      color: '#333',
                      padding: '0.2rem 0.4rem',
                      borderRadius: '15px',
                      fontSize: '0.6rem',
                      fontWeight: 'bold',
                      marginBottom: '0.3rem',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      display: 'inline-block',
                      minWidth: 'auto',
                      whiteSpace: 'nowrap',
                      maxWidth: '70px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {position}
                    </div>
                    <div style={{
                      position: 'relative',
                      display: 'inline-block'
                    }}>
                      <img 
                        src={getCardImageSrc(cardData)}
                        alt={cardName}
                        style={{
                          width: '60px',
                          height: 'auto',
                          borderRadius: '4px',
                          transform: gridPosition.transform ? 
                            (isReversed ? `${gridPosition.transform} rotate(180deg)` : gridPosition.transform) :
                            (isReversed ? 'rotate(180deg)' : 'none'),
                          border: '1px solid #ddd',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                          transition: 'transform 0.3s ease'
                        }}
                        onError={(e) => {
                          e.target.src = '/images/card_back.jpg';
                        }}
                      />
                      {isReversed && (
                        <div style={{
                          position: 'absolute',
                          top: '-3px',
                          right: '-3px',
                          background: '#e74c3c',
                          color: 'white',
                          borderRadius: '50%',
                          width: '14px',
                          height: '14px',
                          fontSize: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}>
                          R
                        </div>
                      )}
                    </div>
                    <div style={{
                      fontSize: '0.6rem',
                      color: '#333',
                      marginTop: '0.3rem',
                      fontWeight: '500',
                      lineHeight: '1.1',
                      maxWidth: '70px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {cardName.replace(/^Reversed /, '')}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Regular grid layout for other spreads */
            <div className="spread-layout" style={{
              display: 'grid',
              gap: window.innerWidth <= 768 ? '0.8rem' : '1rem',
              justifyContent: 'center',
              alignItems: 'center',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 
                                 reading.cards.length === 1 ? '1fr' : 
                                 reading.cards.length === 2 ? 'repeat(2, 1fr)' :
                                 reading.cards.length === 4 ? 'repeat(2, 1fr)' :
                                 reading.cards.length === 6 ? 'repeat(3, 1fr)' :
                                 'repeat(auto-fit, minmax(80px, 1fr))',
              maxWidth: window.innerWidth <= 768 ? '100%' : '600px',
              margin: '0 auto'
            }}>
              {reading.cards.map((cardData, index) => {
                const cardName = cardData.card?.name || cardData.name || 'Unknown Card';
                
                // Multi-language reversed detection function
                const isCardReversed = (cardData, cardName) => {
                  // Check all possible reversed indicators
                  const reversedTranslations = ['reversed', '逆位置', '逆位'];
                  
                  // Check card.position
                  if (cardData.card?.position && reversedTranslations.includes(cardData.card.position)) {
                    return true;
                  }
                  
                  // Check cardData.position
                  if (cardData.position && reversedTranslations.includes(cardData.position)) {
                    return true;
                  }
                  
                  // Check if card name starts with "Reversed" in English
                  if (cardName.startsWith('Reversed')) {
                    return true;
                  }
                  
                  return false;
                };
                
                const isReversed = isCardReversed(cardData, cardName);
                
                // Get the position name - this should be the spread position like "Past", "Present", "Future", etc.
                const position = cardData.position || 
                               (cardData.card && cardData.card.position && !['upright', 'reversed'].includes(cardData.card.position) ? cardData.card.position : null) ||
                               `${t('spreadDrawing.cardPosition')} ${index + 1}`;
                
                // Get card image source
                const getCardImageSrc = (cardData) => {
                  // If card has image URL, convert it to local path
                  if (cardData.card?.image) {
                    const imageUrl = cardData.card.image;
                    const fileName = imageUrl.split('/').pop();
                    return `/images/tarot-cards/${fileName}`;
                  }
                  
                  // Fallback: try to extract card number from name and use local image
                  const cardName = cardData.card?.name || cardData.name || '';
                  const match = cardName.match(/^(\d+)\./);
                  if (match) {
                    return `/images/tarot-cards/image_${match[1]}.jpg`;
                  }
                  
                  // Final fallback
                  return '/images/tarot-cards/image_1.jpg';
                };
                
                return (
                  <div key={index} className="spread-card-mini" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    <div style={{
                      background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                      color: '#333',
                      padding: '0.3rem 0.6rem',
                      borderRadius: '20px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      marginBottom: '0.5rem',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      display: 'inline-block',
                      minWidth: 'auto',
                      whiteSpace: 'nowrap'
                    }}>
                      {position}
                    </div>
                    <div style={{
                      position: 'relative',
                      display: 'inline-block'
                    }}>
                      <img 
                        src={getCardImageSrc(cardData)}
                        alt={cardName}
                        style={{
                          width: window.innerWidth <= 480 ? '60px' : '80px',
                          height: 'auto',
                          borderRadius: '6px',
                          transform: isReversed ? 'rotate(180deg)' : 'none',
                          border: '1px solid #ddd',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          transition: 'transform 0.3s ease'
                        }}
                        onError={(e) => {
                          e.target.src = '/images/card_back.jpg';
                        }}
                      />
                      {isReversed && (
                        <div style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: '#e74c3c',
                          color: 'white',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          fontSize: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}>
                          R
                        </div>
                      )}
                    </div>
                    <div style={{
                      fontSize: '0.7rem',
                      color: '#333',
                      marginTop: '0.5rem',
                      fontWeight: '500',
                      lineHeight: '1.2'
                    }}>
                      {cardName.replace(/^Reversed /, '')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="cards-section">
          <div className="cards-display">
            {reading.cards.map((cardData, index) => (
              <CardDisplay key={index} cardData={cardData} />
            ))}
          </div>
        </div>

        {/* Ask Taroca Section - Show when no AI explanation exists */}
        {!reading.aiExplanation && (
          <div className="general-reading-section" style={{ 
            marginTop: '2rem', 
            marginBottom: '2rem',
            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05), rgba(184, 134, 11, 0.05))',
            border: '2px solid rgba(212, 175, 55, 0.3)'
          }}>
            <div className="category-icon">
              <img 
                src="/images/taroca_favicon.png" 
                alt="Ask Taroca"
                className="category-icon-img"
                style={{ width: '32px', height: '32px' }}
              />
            </div>
            <h3 className="general-reading-title" style={{ color: '#D4AF37' }}>
              {t('readingDetail.askTarocaTitle')}
            </h3>
            <p className="general-reading-description" style={{ marginBottom: '15px' }}>
              {t('readingDetail.askTarocaDesc')}
            </p>
            
            {/* Benefits List */}
            <div style={{ 
              textAlign: 'left', 
              maxWidth: '400px', 
              margin: '0 auto 20px',
              color: '#6b7280'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <Sparkles size={16} style={{ color: '#D4AF37', marginRight: '8px' }} />
                <span>{t('readingDetail.benefit1')}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <Sparkles size={16} style={{ color: '#D4AF37', marginRight: '8px' }} />
                <span>{t('readingDetail.benefit2')}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <Sparkles size={16} style={{ color: '#D4AF37', marginRight: '8px' }} />
                <span>{t('readingDetail.benefit3')}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <Sparkles size={16} style={{ color: '#D4AF37', marginRight: '8px' }} />
                <span>{t('readingDetail.benefit4')}</span>
              </div>
            </div>

            <button 
              className="general-reading-btn"
              onClick={handleAskTaroca}
              disabled={isGeneratingAI}
              style={{
                background: isGeneratingAI ? '#ccc' : 'linear-gradient(135deg, #D4AF37, #B8860B)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '25px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: isGeneratingAI ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.3s ease'
              }}
            >
              <Sparkles size={20} className={isGeneratingAI ? 'animate-spin' : ''} />
              {isGeneratingAI ? t('readingDetail.tarocaRevealing') : t('readingDetail.askTarocaBtn')}
            </button>
          </div>
        )}

        {reading.aiExplanation && (
          <div id="taroca-explanation" className="ai-explanation-section">
            <h3>{t('readingDetail.tarocaExplanation')}</h3>
            <div style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05), rgba(184, 134, 11, 0.05))',
              border: '1px solid rgba(212, 175, 55, 0.2)',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '0.5rem'
            }}>
              <AIExplanationDisplay explanation={reading.aiExplanation} />
            </div>
          </div>
        )}

        {aiError && (
          <div className="ai-error-section" style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '8px',
            padding: '1rem',
            marginTop: '1rem',
            color: '#dc2626'
          }}>
            <p style={{ margin: 0 }}>
              <strong>{t('readingDetail.aiError')}</strong> {aiError}
            </p>
          </div>
        )}
      </div>
      
      {/* Payment Modal for AI Explanation */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          spreadData={reading ? {
            spreadName: reading.spread || 'Custom Reading',
            spreadType: reading.spreadType || reading.spread || 'custom',
            cards: reading.cards.map(cardData => ({
              name: cardData.card.name,
              meaning: cardData.card.meaning,
              position: cardData.card.position || 'upright',
              image: cardData.card.image,
              link: cardData.card.link
            })),
            question: reading.question || '',
            notes: reading.notes || '',
            cardCount: reading.cards.length,
            date: reading.date,
            userId: reading.userId
          } : {}}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

const CardDisplay = ({ cardData }) => {
  const { t } = useLanguage();
  
  // Helper function to check if card is reversed in any language
  const isReversed = (position) => {
    const reversedTranslations = ['reversed', '逆位置', '逆位'];
    return reversedTranslations.includes(position);
  };
  
  return (
    <div className="card-display">
      <div className="card-position-label">
        {cardData.position}
      </div>
      
      <div className="card-image-container">
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img 
            src={cardData.card.image} 
            alt={cardData.card.name}
            className={`card-image ${isReversed(cardData.card.position) ? 'reversed' : ''}`}
          />
          {isReversed(cardData.card.position) && (
            <div style={{
              position: 'absolute',
              top: '0px',
              right: '0px',
              background: 'rgb(231, 76, 60)',
              color: 'white',
              borderRadius: '50%',
              width: '16px',
              height: '16px',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              zIndex: 10,
              transform: 'translate(50%, -50%)'
            }}>
              R
            </div>
          )}
        </div>
      </div>
      
      <div className="card-info">
        <h4 className="card-name">
          {cardData.card.name}
        </h4>
        <p className="card-meaning">
          {cardData.card.meaning}
        </p>
        
        {/* Learn More Link */}
        {cardData.card.link && (
          <div style={{
            textAlign: 'center',
            marginTop: '1rem'
          }}>
            <a 
              href={cardData.card.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#C0AC93',
                borderStyle: 'dashed',
                borderWidth: '1px',
                borderColor: '#C0AC93',
                borderRadius: '0px',
                padding: '15px 30px',
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.85rem',
                fontWeight: '500',
                textDecoration: 'none',
                background: 'transparent',
                transition: 'all 0.3s ease',
                boxShadow: 'none'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = 'rgba(192, 172, 147, 0.1)';
                e.target.style.borderColor = '#261E1E';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = '#C0AC93';
              }}
            >
              {t('readingDetail.learnMore')}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingDetail;
